import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import styles from './ReservationTripsSection.module.scss';
import VehicleReservationsService from 'api/vehicleReservations/VehicleReservationsService';
import { ReservationTripItemViewModel } from 'api/vehicleReservations/models/ReservationTripItemViewModel';
import IconWithText from 'common/components/iconWithText/IconWithText';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import GaugeIcon from 'assets/svg/gauge.svg';
import PlayIcon from 'assets/svg/play2.svg';
import HourGlassIcon from 'assets/svg/hour-glass.svg';
import FlagBWIcon from 'assets/svg/flag_bw.svg';
import colors from 'styles/export/colors.module.scss';

interface ReservationTripsSectionProps {
    vehicleId: string;
    startDate: Date;
    endDate: Date;
    reservationId: string;
}

const ITEMS_PER_PAGE = 10;

const ReservationTripsSection: React.FC<ReservationTripsSectionProps> = ({ vehicleId, startDate, endDate, reservationId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();

    const [isOpen, setIsOpen] = useState(false);
    const [trips, setTrips] = useState<ReservationTripItemViewModel[]>([]);
    const [reachedEnd, setReachedEnd] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const loadTrips = useCallback(async (page: number) => {
        try {
            setIsLoading(true);
            const criteria = {
                itemsPerPage: ITEMS_PER_PAGE,
                page,
                orderBy: 'desc',
                orderColumn: 'v.verification_date',
                vehicleId,
                tripStartDate: startDate,
                tripEndDate: endDate,
                reservationId
            };

            const result = await VehicleReservationsService.getReservationTrips(criteria);
            setTrips(prev => page > 1 ? [...prev, ...result.items] : result.items);
            setReachedEnd(result.totalPages === 0 || result.currentPage === result.totalPages);
            setCurrentPage(page);
            setHasLoaded(true);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get reservation trips list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [vehicleId, startDate, endDate, reservationId, addToast, t]);

    useEffect(() => {
        if (isOpen && !hasLoaded) {
            loadTrips(1);
        }
    }, [isOpen, hasLoaded, loadTrips]);

    const onTripClick = (trip: ReservationTripItemViewModel) => {
        if (trip.source === 'GPS') {
            const tripDate = moment(trip.startDate).format('YYYY-MM-DD');
            history.push(`/vehicles/trip/details/${vehicleId}/gps/list/${tripDate}/${trip.id}/none`);
        } else {
            history.push(`/verifications/details/${trip.id}`);
        }
    };

    const onLoadMore = () => {
        loadTrips(currentPage + 1);
    };

    const formatTotalMinutes = (time: number) => {
        const hours = Math.floor(time / 60);
        const minutes = Math.round(time % 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}  h`;
    };

    const getDateBegin = (row: ReservationTripItemViewModel) => {
        const dateBegin = row.startDate ? moment(row.startDate).toDate() : null;
        if (dateBegin && row.startHour && row.startHour.length > 0) {
            const parts = row.startHour.split(':');
            dateBegin.setHours(Number(parts[0] ?? 0));
            dateBegin.setMinutes(Number(parts[1] ?? 0));
        }
        return dateBegin;
    };

    const getDateEnd = (row: ReservationTripItemViewModel) => {
        const dateEnd = row.endDate ? moment(row.endDate).toDate() : null;
        if (dateEnd && row.endHour) {
            const parts = row.endHour.split(':');
            dateEnd.setHours(Number(parts[0] ?? 0));
            dateEnd.setMinutes(Number(parts[1] ?? 0));
        }
        return dateEnd;
    };

    return (
        <div className={styles.container}>
            <div className={styles.tripsBar} onClick={() => setIsOpen(!isOpen)}>
                <span className={styles.toggleIcon}>{isOpen ? <FaAngleDown /> : <FaAngleRight />}</span>
                <span>{t('reservations.trips_section_title')}</span>
            </div>

            {isOpen && (
                <div className={styles.tripsContent}>
                    {isLoading && (
                        <div className={styles.loading}>{t('common.loading')}...</div>
                    )}
                    {!isLoading && hasLoaded && trips.length === 0 && (
                        <div className={styles.noResults}>
                            {t('car_verification.list.without_trips')}
                        </div>
                    )}
                    {!isLoading && trips.map((trip, index) => {
                        const dateBegin = getDateBegin(trip);
                        const dateEnd = getDateEnd(trip);

                        return (
                            <div
                                key={trip.id || index}
                                className={styles.tripRow}
                                onClick={() => onTripClick(trip)}
                            >
                                <div className={styles.tripCells}>
                                    <div className={styles.tripDatesCell}>
                                        {trip.source === 'GPS' && (
                                            <div className={styles.tripDateHeader}>
                                                <span className={styles.gpsBadge}>
                                                    {t('reservations.trip_source_gps') as string}
                                                </span>
                                            </div>
                                        )}
                                        <div data-tip={t('car_verification.list.kms_traveled_travel_duration_tooltip')} data-for={`trip_1_${trip.id}`}>
                                            <IconWithText
                                                icon={GaugeIcon}
                                                value={trip.totalKms != undefined ? trip.totalKms : '-'}
                                                color='black'
                                                isNumeric={true}
                                                suffix={`${t('car_verification.list.kms')} / ${formatTotalMinutes(trip.totalMinutes ?? 0)}`}
                                            />
                                            <ReactTooltip id={`trip_1_${trip.id}`} />
                                        </div>
                                        {dateBegin ? (
                                            <div data-tip={t('car_verification.list.start_travel_date_tooltip')} data-for={`trip_2_${trip.id}`}>
                                                <IconWithText icon={PlayIcon} valueDate={dateBegin} valueHour={trip.startHour} isDate={true} />
                                                <ReactTooltip id={`trip_2_${trip.id}`} />
                                            </div>
                                        ) : <>-</>}
                                        {dateBegin && !dateEnd ? (
                                            <IconWithText
                                                icon={HourGlassIcon}
                                                value={t('car_verification.list.in_course')}
                                                suffix={t('car_verification.list.kms')}
                                                color={colors.defaultGreen}
                                                className={styles.labelSmall}
                                            />
                                        ) : dateBegin && dateEnd ? (
                                            <div data-tip={t('car_verification.list.end_travel_date_tooltip')} data-for={`trip_4_${trip.id}`}>
                                                <IconWithText icon={FlagBWIcon} valueDate={dateEnd} valueHour={trip.endHour ?? null} isDate={true} />
                                                <ReactTooltip id={`trip_4_${trip.id}`} />
                                            </div>
                                        ) : <></>}
                                        {trip.source === 'GPS' && (trip.startAddress || trip.endAddress) && (
                                            <div className={styles.tripAddresses}>
                                                {trip.startAddress && <span>{trip.startAddress}</span>}
                                                {trip.startAddress && trip.endAddress && <span> → </span>}
                                                {trip.endAddress && <span>{trip.endAddress}</span>}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.tripDriverCell} data-tip={t('reservations.list.driver')} data-for={`trip_driver_${trip.id}`}>
                                        <div>
                                            <AvatarWithText src={trip.driverPhotoUrl}
                                                size="x2"
                                                hideAvatar={!trip.driverName}><div className={styles.bold}>{trip.driverName || '-'}</div></AvatarWithText>
                                        </div>
                                        <div className={styles.driverEmail}>{trip.driverEmail}</div>
                                        <ReactTooltip id={`trip_driver_${trip.id}`} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {!isLoading && !reachedEnd && hasLoaded && (
                        <div className={styles.loadMoreBtn} onClick={onLoadMore}>
                            {t('common.load_more')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReservationTripsSection;
